import React from 'react'
import * as ROUTES from 'constants/routes'
import { Box, Label, Link, Table, Text } from '@workwhile/ui'
import { ColumnDef } from '@tanstack/react-table'
import { formatInTimeZone } from 'date-fns-tz'
import { JetAssignmentItem } from 'api/admin/jet/listJetAssignments'
import { JetAssignmentStatusBadge } from './JetAssignmentStatusBadge'

// jet_assignment timestamps are UTC instants with no per-row timezone, so they
// are rendered in WorkWhile HQ time (mirrors the backend ADMIN_TZ).
const DISPLAY_TZ = 'America/Los_Angeles'

const formatDateTime = (value: string) =>
  formatInTimeZone(value, DISPLAY_TZ, 'MMM d, yyyy h:mm a z')

// shift_id/association_id/worker are only populated once an assignment is grouped
// into a shift and bound to a worker, so these cells link when present, dash if
// not. Hyperlinks match the Jet Alerts page (shift + worker detail pages).
const ShiftCell = ({ shiftId }: { shiftId: number | null }) => {
  if (shiftId === null) {
    return <Text>-</Text>
  }
  return (
    <Link
      to={ROUTES.SHIFT_DETAIL.replace(':shiftId', String(shiftId))}
      target="_blank"
    >
      {shiftId}
    </Link>
  )
}

const WorkerCell = ({
  workerId,
  workerName,
}: {
  workerId: number | null
  workerName: string | null
}) => {
  if (workerId === null) {
    return <Text>-</Text>
  }
  return (
    <Link
      to={ROUTES.USER_DETAIL.replace(':userId', String(workerId))}
      target="_blank"
      style={{ whiteSpace: 'nowrap' }}
    >
      {workerName ? `${workerName} (${workerId})` : `#${workerId}`}
    </Link>
  )
}

const columns: ColumnDef<JetAssignmentItem>[] = [
  {
    header: 'Instance',
    accessorKey: 'jetInstanceName',
    cell: ({ row }) => (
      <Text style={{ whiteSpace: 'nowrap' }}>
        {row.original.jetInstanceName}
      </Text>
    ),
  },
  {
    header: 'Assignment ID',
    accessorKey: 'assignmentId',
    cell: ({ row }) => (
      <Text style={{ whiteSpace: 'nowrap' }}>{row.original.assignmentId}</Text>
    ),
  },
  {
    header: 'Job ID',
    accessorKey: 'jobId',
    cell: ({ row }) => (
      <Text style={{ whiteSpace: 'nowrap' }}>{row.original.jobId}</Text>
    ),
  },
  {
    header: 'Status',
    accessorKey: 'status',
    cell: ({ row }) => (
      <JetAssignmentStatusBadge status={row.original.status} />
    ),
  },
  {
    header: 'Call report',
    accessorKey: 'callReportStatus',
    cell: ({ row }) =>
      row.original.callReportStatus ? (
        <JetAssignmentStatusBadge status={row.original.callReportStatus} />
      ) : (
        <Text>-</Text>
      ),
  },
  {
    header: 'Shift ID',
    accessorKey: 'shiftId',
    cell: ({ row }) => <ShiftCell shiftId={row.original.shiftId} />,
  },
  {
    header: 'Shift cancelled',
    accessorKey: 'shiftCancelled',
    cell: ({ row }) =>
      row.original.shiftCancelled ? (
        <Label
          variant="error"
          size="small"
          style={{ whiteSpace: 'nowrap', padding: '0 0.5rem' }}
        >
          Cancelled
        </Label>
      ) : (
        <Text>-</Text>
      ),
  },
  {
    header: 'Association ID',
    accessorKey: 'associationId',
    cell: ({ row }) => <Text>{row.original.associationId ?? '-'}</Text>,
  },
  {
    header: 'Return requested',
    accessorKey: 'returnRequested',
    cell: ({ row }) =>
      row.original.returnRequested ? (
        <Label
          variant="warning"
          size="small"
          style={{ whiteSpace: 'nowrap', padding: '0 0.5rem' }}
        >
          Requested
        </Label>
      ) : (
        <Text>-</Text>
      ),
  },
  {
    header: 'Worker',
    accessorKey: 'workerId',
    cell: ({ row }) => (
      <WorkerCell
        workerId={row.original.workerId}
        workerName={row.original.workerName}
      />
    ),
  },
  {
    header: 'WSA status',
    accessorKey: 'wsaStatus',
    cell: ({ row }) => (
      <Text style={{ whiteSpace: 'nowrap' }}>
        {row.original.wsaStatus ?? '-'}
      </Text>
    ),
  },
  {
    header: 'Target start',
    accessorKey: 'targetStartsAt',
    cell: ({ row }) => (
      <Text style={{ whiteSpace: 'nowrap' }}>
        {formatDateTime(row.original.targetStartsAt)}
      </Text>
    ),
  },
  {
    header: 'Created',
    accessorKey: 'createdAt',
    cell: ({ row }) => (
      <Text style={{ whiteSpace: 'nowrap' }}>
        {formatDateTime(row.original.createdAt)}
      </Text>
    ),
  },
]

type JetAssignmentsTableProps = {
  items: JetAssignmentItem[]
}

export const JetAssignmentsTable = ({ items }: JetAssignmentsTableProps) => {
  return (
    <Box overflowX="auto">
      <Table columns={columns} data={items} textVerticalAlign="top" />
    </Box>
  )
}
